import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert } from 'antd';
import { useI18n } from 'components/I18nProvider';
import { useEventAdminState } from 'components/EventStateProvider';
import { SubtitlesStream } from 'lib/subtitles-stream';
import { createEmptySubtitles } from 'lib/subtitles';
import SubtitlesPanel from 'components/SubtitlesPanel';
import styles from './style.module.scss';
import LanguageSelect from 'components/LanguageSelect';
import { getDefaultBgColor, getDefaultTextColor } from 'pages/SubtitlesPage/subtitles-settings';
import { useAdminTheme } from 'components/AdminThemeProvider';
import { findSnapshotActiveSegment } from 'lib/event-state-merge';
import { isTranslationProfileCustomPromptSupported } from 'api/config';
import { isSegmentTranslationLanguage } from 'lib/language-display';
import TranslationPromptEditor from './TranslationPromptEditor';
const SubtitlesPreview = ({ eventId, translationProfiles }) => {
    const { intl } = useI18n();
    const { colorScheme } = useAdminTheme();
    const { eventSnapshot } = useEventAdminState();
    const subtitlesTheme = colorScheme === 'dark' ? 'dark' : 'light';
    const previewTextColor = getDefaultTextColor(subtitlesTheme);
    const previewBgColor = getDefaultBgColor(subtitlesTheme);
    const streamRef = useRef(null);
    const previewContainerRef = useRef(null);
    const snapshotActiveSegment = useMemo(() => findSnapshotActiveSegment(eventSnapshot), [eventSnapshot]);
    const [subtitles, setSubtitles] = useState(createEmptySubtitles());
    const [selectedLanguage, setSelectedLanguage] = useState(null);
    const [languagePending, setLanguagePending] = useState(true);
    const [autoScroll, setAutoScroll] = useState(true);
    useEffect(() => {
        streamRef.current = new SubtitlesStream(eventId);
        streamRef.current.on('configSync', (pending) => {
            setLanguagePending(pending);
        });
        streamRef.current.on('segmentStarted', () => {
            setSubtitles('started');
        });
        streamRef.current.on('languageChanged', (language) => {
            setSelectedLanguage(language.iso);
        });
        streamRef.current.on('transcriptUpdated', (transcript) => {
            setSubtitles(transcript.subtitles);
            if (previewContainerRef.current) {
                previewContainerRef.current.scrollTop = previewContainerRef.current.scrollHeight;
            }
        });
        streamRef.current.on('segmentEnded', () => {
            setSubtitles(createEmptySubtitles());
        });
        streamRef.current.connect();
        return () => {
            streamRef.current?.disconnect();
            streamRef.current = null;
        };
    }, [eventId]);
    const handleLanguageChange = (language) => {
        streamRef.current?.selectLanguage(language);
    };
    if (snapshotActiveSegment === null) {
        return null;
    }
    const previewLanguage = selectedLanguage ?? snapshotActiveSegment.audioLanguage;
    const showPromptEditor = isSegmentTranslationLanguage(snapshotActiveSegment, previewLanguage)
        && isTranslationProfileCustomPromptSupported(translationProfiles, snapshotActiveSegment.translationSetup.profileId);
    return (_jsxs("div", { className: styles.subtitlesPreview, "data-admin-theme": colorScheme, children: [_jsx(LanguageSelect, { testId: "admin-subtitles-preview-language-select", language: previewLanguage, loading: languagePending, onLanguageChange: handleLanguageChange, segment: snapshotActiveSegment }), _jsx("div", { ref: previewContainerRef, className: styles.previewContainer, children: subtitles === 'started' ? (_jsx(Alert, { className: styles.subtitlesStartedAlert, message: `${intl.formatMessage({ id: 'subtitlesRunning' })}...`, type: "info", showIcon: true })) : (_jsx(SubtitlesPanel, { subtitles: subtitles, settings: {
                        controls: 'auto',
                        fontSize: 'lg',
                        fullscreen: false,
                        theme: subtitlesTheme,
                        textColor: previewTextColor,
                        bgColor: previewBgColor,
                        textAlign: 'top',
                        customCss: '',
                    }, speechSegmentation: false, autoScroll: autoScroll, autoScrollButton: "small", onAutoScrollChange: setAutoScroll, autoscrollButtonTestId: "admin-subtitles-preview-autoscroll-button" })) }), showPromptEditor && (_jsx(TranslationPromptEditor, { eventId: eventId, segmentId: snapshotActiveSegment.id, language: previewLanguage }))] }));
};
export default SubtitlesPreview;
